import BaseAPI from "./BaseAPI";
import {
  ParsedNavigationRequest,
  StringKeyMap,
  parseNavigationRequest,
  stringMatches,
} from "./utilities";
import { BaseCMI } from "./cmi/common/base_cmi";
import { CMIArray } from "./cmi/common/array";
import { CMI } from "./cmi/scorm2004/cmi";
import { CMIObjectivesObject } from "./cmi/scorm2004/objectives";
import { CMIInteractionsObject } from "./cmi/scorm2004/interactions";
import { ADL } from "./cmi/scorm2004/adl";
import { Sequencing } from "./cmi/scorm2004/sequencing/sequencing";
import {
  Activity,
  ActivityObjective,
  ActivityObjectiveReadState,
} from "./cmi/scorm2004/sequencing/activity";
import {
  GlobalObjective,
  GlobalObjectiveSynchronizer,
} from "./cmi/scorm2004/sequencing/objectives/global_objective_synchronizer";
import {
  CompletionStatus,
  global_constants,
  LogLevelEnum,
  scorm2004_constants,
  scorm2004_errors,
  scorm2004_regex,
  SuccessStatus,
} from "./constants";
import {
  ActivitySettings,
  CommitObject,
  CommitTrigger,
  GlobalObjectiveMapEntry,
  ResetOptions,
  ResultObject,
  RollupRulesSettings,
  SequencingCollectionSettings,
  SequencingControlsSettings,
  SequencingRulesSettings,
  SequencingSettings,
  SequencingStateMetadata,
  Settings,
} from "./types";
import { IHttpService } from "./interfaces";
import { SequencingConfiguration, SequencingEventListeners, SequencingService } from "./services";
import type { NavigationPreview } from "./types/sequencing_types";

// Import extracted classes
import {
  Scorm2004ResponseValidator,
  ValidationContext,
} from "./handlers/scorm2004/response_validator";
import { Scorm2004CMIHandler, CMIHandlerContext } from "./handlers/scorm2004/cmi_handler";
import { ActivityTreeBuilder } from "./configuration/activity_tree_builder";
import { SequencingConfigurationBuilder } from "./configuration/sequencing_configuration_builder";
import {
  GlobalObjectiveManager,
  GlobalObjectiveContext,
} from "./objectives/global_objective_manager";
import {
  SequencingStatePersistence,
  PersistenceContext,
} from "./persistence/sequencing_state_persistence";
import {
  Scorm2004DataSerializer,
  DataSerializerContext,
} from "./serialization/scorm2004_data_serializer";

/**
 * Implemented, writable SCORM 2004 elements that have NO spec-defined default
 * value. Per IEEE 1484.11.2 / SCORM 2004 RTE, GetValue of one of these before it
 * has been set must return "" with error 403 (VALUE_NOT_INITIALIZED).
 *
 * Excluded by design: elements with a vocabulary/computed default (mode, credit,
 * entry, completion_status, success_status, total_time), the keyword/identity
 * elements (_version, _children, _count, learner_id, learner_name, launch_data),
 * and array `.id` (a missing array entry already yields 403 during traversal).
 *
 * Array indices are normalized to "N" for lookup (see {@link normalizeCMIIndices}).
 */
const NO_DEFAULT_2004_ELEMENTS: ReadonlySet<string> = new Set<string>([
  "cmi.suspend_data",
  "cmi.location",
  "cmi.scaled_passing_score",
  "cmi.max_time_allowed",
  "cmi.completion_threshold",
  "cmi.progress_measure",
  "cmi.score.scaled",
  "cmi.score.raw",
  "cmi.score.min",
  "cmi.score.max",
  "cmi.objectives.N.score.scaled",
  "cmi.objectives.N.score.raw",
  "cmi.objectives.N.score.min",
  "cmi.objectives.N.score.max",
  "cmi.objectives.N.progress_measure",
  "cmi.objectives.N.description",
  "cmi.interactions.N.weighting",
  "cmi.interactions.N.type",
  "cmi.interactions.N.timestamp",
  "cmi.interactions.N.result",
  "cmi.interactions.N.latency",
  "cmi.interactions.N.learner_response",
  "cmi.interactions.N.description",
  "cmi.comments_from_learner.N.timestamp",
  "adl.data.N.store",
]);

/** Replace each numeric array-index segment with "N" so paths match the table. */
function normalizeCMIIndices(CMIElement: string): string {
  return CMIElement.replace(/\.\d+(?=\.|$)/g, ".N");
}

/**
 * API class for SCORM 2004
 */
class Scorm2004API extends BaseAPI {
  private _version: string = "1.0";
  private readonly _sequencing: Sequencing;
  private _sequencingService: SequencingService | null = null;
  private _restoringFromJSON = false;
  private readonly _runtimeSetCMIElements = new Set<string>();
  private _extractedScoItemIds: string[] = [];
  private _sequencingCollections: Record<string, SequencingCollectionSettings> = {};

  // Extracted class instances
  private _responseValidator: Scorm2004ResponseValidator;
  private _cmiHandler: Scorm2004CMIHandler;
  private _activityTreeBuilder: ActivityTreeBuilder;
  private _sequencingConfigBuilder: SequencingConfigurationBuilder;
  private _globalObjectiveManager: GlobalObjectiveManager;
  private _statePersistence: SequencingStatePersistence | null = null;
  private _dataSerializer: Scorm2004DataSerializer;

  /**
   * Constructor for SCORM 2004 API
   * @param {Settings} settings
   * @param {IHttpService} httpService - Optional HTTP service instance
   */
  constructor(settings?: Settings, httpService?: IHttpService) {
    const settingsCopy = settings ? { ...settings } : undefined;
    if (settingsCopy) {
      if (settingsCopy.mastery_override === undefined) {
        settingsCopy.mastery_override = false;
      }
    }
    super(scorm2004_errors, settingsCopy, httpService);

    this.cmi = new CMI();
    this.adl = new ADL();
    this._sequencing = new Sequencing();

    // Connect the sequencing object to the ADL's sequencing property
    this.adl.sequencing = this._sequencing;

    // Initialize extracted classes
    this._sequencingConfigBuilder = new SequencingConfigurationBuilder();
    this._activityTreeBuilder = new ActivityTreeBuilder({}, this._sequencingConfigBuilder);

    // Create validation context - use arrow functions to ensure lazy evaluation
    // Note: checkCorrectResponseValue calls the validator directly since it's a pass-through
    const validationContext: ValidationContext = {
      throwSCORMError: (element: string, errorCode: number, message?: string) =>
        this.throwSCORMError(element, errorCode, message),
      getLastErrorCode: () => this.lastErrorCode,
      checkCorrectResponseValue: (
        CMIElement: string,
        interaction_type: string,
        nodes: Array<any>,
        value: any,
      ) =>
        this._responseValidator.checkCorrectResponseValue(
          CMIElement,
          interaction_type,
          nodes,
          value,
        ),
    };
    this._responseValidator = new Scorm2004ResponseValidator(validationContext);

    // Create CMI handler context - use arrow functions to ensure spies work correctly
    const cmiHandlerContext: CMIHandlerContext = {
      cmi: this.cmi,
      isInitialized: () => this.isInitialized(),
      throwSCORMError: (element: string, errorCode: number, message?: string) =>
        this.throwSCORMError(element, errorCode, message),
      getLastErrorCode: () => this.lastErrorCode,
    };
    this._cmiHandler = new Scorm2004CMIHandler(cmiHandlerContext, this._responseValidator);

    // Initialize global objective manager context - use getter to always get current settings
    const globalObjectiveContext: GlobalObjectiveContext = {
      getSettings: () => this.settings,
      hostDeclaredGlobalObjectiveIds: [...(settingsCopy?.globalObjectiveIds ?? [])],
      cmi: this.cmi,
      sequencing: this._sequencing,
      sequencingService: this._sequencingService,
      commonSetCMIValue: this._commonSetCMIValue.bind(this),
    };
    this._globalObjectiveManager = new GlobalObjectiveManager(globalObjectiveContext);

    // Initialize data serializer - use getter to always get current settings
    const dataSerializerContext: DataSerializerContext = {
      getSettings: () => this.settings,
      cmi: this.cmi,
      adl: this.adl,
      sequencingService: this._sequencingService,
      renderCMIToJSONObject: this.renderCMIToJSONObject.bind(this),
    };
    this._dataSerializer = new Scorm2004DataSerializer(
      dataSerializerContext,
      this._globalObjectiveManager,
    );

    // Configure sequencing if settings are provided
    if (settingsCopy?.sequencing) {
      this.configureSequencing(settingsCopy.sequencing);
    }

    // Initialize sequencing service
    this.initializeSequencingService(settingsCopy);

    // Rename functions to match 2004 Spec and expose to modules
    this.Initialize = this.lmsInitialize;
    this.Terminate = this.lmsFinish;
    this.GetValue = this.lmsGetValue;
    this.SetValue = this.lmsSetValue;
    this.Commit = this.lmsCommit;
    this.GetLastError = this.lmsGetLastError;
    this.GetErrorString = this.lmsGetErrorString;
    this.GetDiagnostic = this.lmsGetDiagnostic;
  }

  public cmi: CMI;
  public adl: ADL;

  public Initialize: (parameter?: string) => string;
  public Terminate: (parameter?: string) => string;
  public GetValue: (CMIElement: string) => string;
  public SetValue: (CMIElement: string, value: any) => string;
  public Commit: (parameter?: string) => string;
  public GetLastError: () => string;
  public GetErrorString: (CMIErrorCode: string | number) => string;
  public GetDiagnostic: (CMIErrorCode: string | number) => string;

  /**
   * Called when the API needs to be reset
   *
   * @param {Settings} settings - Optional new settings to merge with existing settings
   * @param {ResetOptions} options - Behavior for this reset only
   * @spec SCORM 2004 4th Ed. RTE 4.4 (adl.nav.request_valid) - navigation validity is LMS-determined, not per-SCO run-time data.
   * @spec SN Book: NB.2.1 (Navigation Request Process) - refresh validity for the current sequencing activity after a SCO reset.
   */
  reset(settings?: Settings, options?: ResetOptions) {
    this.commonReset(settings, options);
    this._runtimeSetCMIElements.clear();

    this.cmi?.reset();
    if (options?.resetTotalTime === true) {
      this.cmi.total_time = "PT0S";
    }
    this.adl?.reset();
    this.applyCurrentActivityLaunchData();

    // @spec SCORM 2004 4th Ed. RTE 4.4; SN Book: NB.2.1 - restore the LMS navigation connection and validity only for a delivered activity.
    if (this._sequencing?.getCurrentActivity()) {
      this.adl.sequencing = this._sequencing;
      this._sequencing.overallSequencingProcess?.updateNavigationValidity();
    }
  }

  /**
   * Apply launch-static activity data to CMI while the new SCO is pre-initialize.
   *
   * @spec SCORM 2004 4th Ed. RTE 4.2.5, Table 4.2.5a - cmi.completion_threshold
   * @spec SCORM 2004 4th Ed. RTE 4.2.17, Table 4.2.17a - cmi.objectives
   */
  private applyCurrentActivityLaunchData(): void {
    const currentActivity = this._sequencing?.getCurrentActivity();
    if (!currentActivity) {
      return;
    }

    this.adl.configureSharedDataMaps(currentActivity.sharedDataMaps);
    this.applyActivityLaunchData(currentActivity);
  }

  /**
   * Apply launch-static CMI data when sequencing delivers a new activity before SCO Initialize.
   *
   * @spec SCORM 2004 4th Ed. SN DB.2 - Content Delivery Environment Process establishes the delivered activity.
   * @spec SCORM 2004 4th Ed. RTE 4.2.5, Table 4.2.5a - cmi.completion_threshold is initialized before SCO access.
   * @spec SCORM 2004 4th Ed. RTE 4.2.17, Table 4.2.17a - cmi.objectives is initialized before SCO access.
   */
  private applyDeliveredActivityLaunchData(activity: Activity): void {
    this.adl.configureSharedDataMaps(activity.sharedDataMaps);
    // @spec SCORM 2004 4th Ed. RTE 3.1.6 - API Instance State Transition;
    // launch data is prepared before the SCO opens its communication session.
    if (!this.isNotInitialized()) {
      return;
    }

    this.applyActivityLaunchData(activity);
  }

  /**
   * Copy the delivered activity's static launch data into the fresh CMI model.
   *
   * @spec SCORM 2004 4th Ed. RTE 4.2.5, Table 4.2.5a - cmi.completion_threshold
   * @spec SCORM 2004 4th Ed. RTE 4.2.17, Table 4.2.17a - cmi.objectives
   */
  private applyActivityLaunchData(currentActivity: Activity): void {
    if (!this.cmi) {
      return;
    }

    const contentActivityData =
      this._sequencing.overallSequencingProcess?.getContentActivityData(currentActivity);
    const completionThreshold =
      contentActivityData?.completionThreshold ??
      currentActivity.completionThreshold?.toString() ??
      "";

    this.cmi.completion_threshold = completionThreshold;
    this.seedCurrentActivityObjectives(currentActivity);
  }

  /**
   * Seed CMI objective ids after Initialize when automatic sequencing starts during Initialize.
   *
   * @spec SCORM 2004 4th Ed. RTE 4.2.17, Table 4.2.17a - cmi.objectives
   */
  private applyCurrentActivityObjectiveData(): void {
    const currentActivity = this._sequencing?.getCurrentActivity();
    if (!this.cmi || !currentActivity) {
      return;
    }

    this.seedCurrentActivityObjectives(currentActivity);
  }

  /**
   * Seed CMI objectives from primary and secondary activity objectives.
   *
   * @spec SCORM 2004 4th Ed. RTE 4.2.17 - Initialization of Run-Time Objectives from Sequencing Information
   * @spec SCORM 2004 4th Ed. RTE 4.2.17, Table 4.2.17a - cmi.objectives.n.id and success_status
   * @spec SCORM 2004 4th Ed. SN 3.10.3 / ADLSEQ objectives extension - objective read maps seed the RTE view
   */
  private seedCurrentActivityObjectives(currentActivity: Activity): void {
    const activityObjectives: ActivityObjective[] = [];

    if (currentActivity.primaryObjective) {
      activityObjectives.push(currentActivity.primaryObjective);
    }

    activityObjectives.push(...currentActivity.objectives);

    const seededObjectiveIds = new Set<string>();
    for (const activityObjective of activityObjectives) {
      const objectiveId = this.getSeedableObjectiveId(activityObjective);
      if (!objectiveId || seededObjectiveIds.has(objectiveId)) {
        continue;
      }
      seededObjectiveIds.add(objectiveId);

      const index = this.findOrSeedCMIObjective(objectiveId);
      if (index === null) {
        continue;
      }

      const cmiObjective = this.cmi.objectives.findObjectiveByIndex(index);
      if (cmiObjective && this.cmi.objectives.initialized && !cmiObjective.initialized) {
        cmiObjective.initialize();
      }

      const successStatus = this.getActivityObjectiveSuccessStatus(activityObjective);
      if (successStatus) {
        this._commonSetCMIValue(
          "SeedActivityObjective",
          true,
          `cmi.objectives.${index}.success_status`,
          successStatus,
        );
      }

      this.seedObjectiveReadMapValues(currentActivity, activityObjective, index);
    }
  }

  /**
   * Seed CMI objective fields from this objective's read-mapped global objectives.
   *
   * @spec SCORM 2004 4th Ed. RTE 4.2.17 - Run-Time Objectives are initialized from sequencing information
   * @spec SCORM 2004 4th Ed. SN 3.10.3 - read mapInfo grants access to mapped global objective state
   * @spec SCORM 2004 4th Ed. ADLSEQ objectives extension - raw/min/max score read maps seed RTE score fields
   */
  private seedObjectiveReadMapValues(
    currentActivity: Activity,
    activityObjective: ActivityObjective,
    objectiveIndex: number,
  ): void {
    const globalObjectiveMap = this._sequencing.overallSequencingProcess?.getGlobalObjectiveMap();
    if (!globalObjectiveMap || activityObjective.mapInfo.length === 0) {
      return;
    }

    for (const mapInfo of activityObjective.mapInfo) {
      const targetObjectiveId = mapInfo.targetObjectiveID || activityObjective.id;
      const globalObjective = globalObjectiveMap.get(targetObjectiveId) as
        GlobalObjective | undefined;
      if (!globalObjective) {
        continue;
      }

      const readState = GlobalObjectiveSynchronizer.getGlobalObjectiveReadState(
        currentActivity,
        activityObjective,
        mapInfo,
        globalObjective,
        // @spec SCORM 2004 4th Ed. SN 3.10.3: read and write mapInfo flags are
        // independent; delivery initializes the local objective from the global value.
        {
          restrictToFreshWrites: false,
          allowSatisfiedStatus: true,
          allowNormalizedMeasure: true,
        },
      );
      this.applyObjectiveReadStateToCMI(objectiveIndex, readState);
    }
  }

  /**
   * Copy mapped global objective state into the seeded CMI objective entry.
   *
   * @spec SCORM 2004 4th Ed. RTE 4.2.17, Table 4.2.17a - cmi.objectives launch-time initialization
   * @spec SCORM 2004 4th Ed. SN 3.10.3 - read maps populate the RTE view without creating local writes
   * @spec SCORM 2004 4th Ed. ADLSEQ objectives extension - raw/min/max score read maps populate CMI objective scores
   */
  private applyObjectiveReadStateToCMI(
    objectiveIndex: number,
    readState: ActivityObjectiveReadState,
  ): void {
    // @spec SCORM 2004 4th Ed. SN 3.10.3 - unknown global satisfied status
    // leaves cmi.objectives.n.success_status at its default unknown value.
    if (readState.satisfiedStatus !== undefined) {
      this._commonSetCMIValue(
        "SeedActivityObjectiveReadMap",
        true,
        `cmi.objectives.${objectiveIndex}.success_status`,
        readState.satisfiedStatus ? SuccessStatus.PASSED : SuccessStatus.FAILED,
      );
    }

    // @spec SCORM 2004 4th Ed. SN 3.10.3 - unknown global normalized measure
    // leaves cmi.objectives.n.score.scaled uninitialized.
    if (readState.normalizedMeasure !== undefined) {
      this._commonSetCMIValue(
        "SeedActivityObjectiveReadMap",
        true,
        `cmi.objectives.${objectiveIndex}.score.scaled`,
        String(readState.normalizedMeasure),
      );
    }

    // @spec SCORM 2004 4th Ed. SN 3.10.3 - unknown global completion status
    // leaves cmi.objectives.n.completion_status at its default unknown value.
    if (readState.completionStatus !== undefined) {
      this._commonSetCMIValue(
        "SeedActivityObjectiveReadMap",
        true,
        `cmi.objectives.${objectiveIndex}.completion_status`,
        readState.completionStatus,
      );
    }

    // @spec SCORM 2004 4th Ed. SN 3.10.3 - unknown global progress measure
    // leaves cmi.objectives.n.progress_measure uninitialized.
    if (readState.progressMeasure !== undefined) {
      this._commonSetCMIValue(
        "SeedActivityObjectiveReadMap",
        true,
        `cmi.objectives.${objectiveIndex}.progress_measure`,
        String(readState.progressMeasure),
      );
    }

    // @spec SCORM 2004 4th Ed. ADLSEQ objectives extension - unknown global
    // raw score leaves cmi.objectives.n.score.raw uninitialized.
    if (readState.rawScore !== undefined) {
      this._commonSetCMIValue(
        "SeedActivityObjectiveReadMap",
        true,
        `cmi.objectives.${objectiveIndex}.score.raw`,
        readState.rawScore,
      );
    }

    // @spec SCORM 2004 4th Ed. ADLSEQ objectives extension - unknown global
    // min score leaves cmi.objectives.n.score.min uninitialized.
    if (readState.minScore !== undefined) {
      this._commonSetCMIValue(
        "SeedActivityObjectiveReadMap",
        true,
        `cmi.objectives.${objectiveIndex}.score.min`,
        readState.minScore,
      );
    }

    // @spec SCORM 2004 4th Ed. ADLSEQ objectives extension - unknown global
    // max score leaves cmi.objectives.n.score.max uninitialized.
    if (readState.maxScore !== undefined) {
      this._commonSetCMIValue(
        "SeedActivityObjectiveReadMap",
        true,
        `cmi.objectives.${objectiveIndex}.score.max`,
        readState.maxScore,
      );
    }
  }

  /**
   * Return a manifest-defined objective id that can initialize cmi.objectives.n.id.
   *
   * @spec SCORM 2004 4th Ed. RTE 4.2.17, Table 4.2.17a - cmi.objectives.n.id
   */
  private getSeedableObjectiveId(activityObjective: ActivityObjective): string | null {
    const objectiveId: unknown = activityObjective.id;
    if (typeof objectiveId !== "string" || objectiveId.trim() === "") {
      return null;
    }
    return objectiveId;
  }

  /**
   * Find an existing CMI objective id or create the next CMI objective array entry.
   *
   * @spec SCORM 2004 4th Ed. RTE 4.2.17, Table 4.2.17a - cmi.objectives._count
   * @spec SCORM 2004 4th Ed. RTE 4.2.17, Table 4.2.17a - cmi.objectives.n.id
   */
  private findOrSeedCMIObjective(objectiveId: string): number | null {
    const existingIndex = this.cmi.objectives.childArray.findIndex((objective) => {
      return (objective as CMIObjectivesObject).id === objectiveId;
    });

    if (existingIndex >= 0) {
      return existingIndex;
    }

    const index = this.cmi.objectives.childArray.length;
    const result = this._commonSetCMIValue(
      "SeedActivityObjective",
      true,
      `cmi.objectives.${index}.id`,
      objectiveId,
    );

    return result === global_constants.SCORM_TRUE ? index : null;
  }

  /**
   * Translate a known activity objective satisfied status to cmi.objectives.n.success_status.
   *
   * @spec SCORM 2004 4th Ed. RTE 4.2.17, Table 4.2.17a - cmi.objectives.n.success_status
   */
  private getActivityObjectiveSuccessStatus(
    activityObjective: ActivityObjective,
  ): SuccessStatus | null {
    if (activityObjective.progressStatus || activityObjective.satisfiedStatusKnown) {
      return activityObjective.satisfiedStatus ? SuccessStatus.PASSED : SuccessStatus.FAILED;
    }

    return null;
  }

  /**
   * Getter for _version
   * @return {string}
   */
  get version(): string {
    return this._version;
  }

  /**
   * Getter for _globalObjectives
   * @return {CMIObjectivesObject[]} Array of global objective objects
   */
  get globalObjectives(): CMIObjectivesObject[] {
    return this._globalObjectiveManager.globalObjectives;
  }

  /**
   * Setter for _globalObjectives (for backward compatibility)
   * @param {CMIObjectivesObject[]} objectives - Array of global objective objects
   */
  set _globalObjectives(objectives: CMIObjectivesObject[]) {
    this._globalObjectiveManager.globalObjectives = objectives;
  }

  /**
   * Getter for _globalObjectives (for backward compatibility with tests using bracket notation)
   * @return {CMIObjectivesObject[]} Array of global objective objects
   */
  get _globalObjectives(): CMIObjectivesObject[] {
    return this._globalObjectiveManager.globalObjectives;
  }

  /**
   * Restore LMS-persisted global objective values before SCO initialization.
   *
   * @param {Record<string, GlobalObjectiveMapEntry>} snapshot - Persisted objective map values
   *
   * @spec SCORM 2004 4th Ed. SN 3.10.3 - read-mapped objectives use shared global state
   */
  restoreGlobalObjectiveSnapshot(snapshot: Record<string, GlobalObjectiveMapEntry>): void {
    this._globalObjectiveManager.restoreGlobalObjectiveSnapshot(snapshot);
  }

  /** Restore the LMS-persisted SCORM 2004 shared-data stores. */
  restoreSharedDataSnapshot(snapshot: Record<string, string>): void {
    this.adl.restoreSharedDataSnapshot(snapshot);
  }

  /** Capture all SCORM 2004 shared-data stores, including non-current mappings. */
  captureSharedDataSnapshot(): Record<string, string> {
    return this.adl.captureSharedDataSnapshot();
  }

  /**
   * Compress state data (delegates to persistence class)
   * @param {string} data - Data to compress
   * @return {string} Compressed data
   */
  compressStateData(data: string): string {
    if (this._statePersistence) {
      return this._statePersistence.compressStateData(data);
    }
    // Fallback: simple base64 encoding
    if (typeof btoa !== "undefined") {
      return btoa(encodeURIComponent(data));
    }
    return data;
  }

  /**
   * Decompress state data (delegates to persistence class)
   * @param {string} data - Data to decompress
   * @return {string} Decompressed data
   */
  decompressStateData(data: string): string {
    if (this._statePersistence) {
      return this._statePersistence.decompressStateData(data);
    }
    // Fallback: simple base64 decoding
    if (typeof atob !== "undefined") {
      try {
        return decodeURIComponent(atob(data));
      } catch {
        return data;
      }
    }
    return data;
  }

  /**
   * Initialize - Begins a communication session with the LMS
   *
   * @param {string} parameter - Must be an empty string per SCORM 2004 specification
   * @return {string} "true" or "false"
   */
  lmsInitialize(parameter: string = ""): string {
    if (parameter !== "") {
      this.throwSCORMError("api", this._error_codes.ARGUMENT_ERROR);
      return global_constants.SCORM_FALSE;
    }

    this.cmi.initialize();
    const result = this.initialize(
      "Initialize",
      "LMS was already initialized!",
      "LMS is already finished!",
    );

    if (result === global_constants.SCORM_TRUE) {
      this.adl.initialize();
    }

    if (result === global_constants.SCORM_TRUE && this._sequencingService) {
      this._sequencingService.initialize();
      this.applyCurrentActivityObjectiveData();
    }

    if (result === global_constants.SCORM_TRUE) {
      this._globalObjectiveManager.restoreGlobalObjectivesToCMI();
    }

    if (
      result === global_constants.SCORM_TRUE &&
      this.settings.sequencingStatePersistence &&
      this.settings.sequencingStatePersistence.autoLoadOnInitialize !== false
    ) {
      // @spec SCORM 2004 4th Ed. SN 4.2 Tracking Model Persistence - an LMS that must
      // restore tracking before choosing the first delivery can preload it explicitly and
      // suppress this compatibility auto-load. Initialize remains synchronous per the RTE API.
      this.loadSequencingState().catch(() => {
        this.apiLog("lmsInitialize", "Failed to auto-load sequencing state", LogLevelEnum.WARN);
      });
    }

    return result;
  }

  /**
   * Terminate - Ends the communication session and persists data
   *
   * @param {string} parameter - Must be an empty string per SCORM 2004 specification
   * @return {string} "true" or "false"
   */
  lmsFinish(parameter: string = ""): string {
    if (parameter !== "") {
      this.throwSCORMError("api", this._error_codes.ARGUMENT_ERROR);
      return global_constants.SCORM_FALSE;
    }

    const pendingNavRequest = this.adl?.nav?.request || "_none_";
    const exitType = this.cmi?.getExitValueInternal() || "";
    const wasAlreadyTerminated = this.isTerminated();
    const deliveryInProgress = this._sequencingService?.isDeliveryInProgress() ?? false;
    let normalizedRequest = pendingNavRequest;
    let normalizedTarget = "";
    const choiceJumpRegex = new RegExp(scorm2004_regex.NAVEvent);

    if (pendingNavRequest !== "_none_") {
      const matches = pendingNavRequest.match(choiceJumpRegex);
      if (matches) {
        if (matches.groups?.choice_target) {
          normalizedTarget = matches.groups?.choice_target;
          normalizedRequest = "choice";
        } else if (matches.groups?.jump_target) {
          normalizedTarget = matches.groups?.jump_target;
          normalizedRequest = "jump";
        }
      }
    }

    let requestToProcess: string | null = null;
    let targetForProcessing: string | undefined;
    if (normalizedRequest !== "_none_") {
      requestToProcess = normalizedRequest;
      targetForProcessing = normalizedTarget || undefined;
    } else if (this._sequencing.getCurrentActivity()) {
      requestToProcess = "exit";
    }

    // Run termination tracking and objective-map writes before serializing the
    // termination commit, but defer sequencing/delivery until the commit payload
    // has been captured. Delivery listeners may synchronously reset CMI for the
    // next SCO.
    // @spec SCORM 2004 4th Ed. SN OP.1 and SM.7 - termination and objective map
    //   writes precede sequencing/delivery and are persisted at the session boundary.
    const preparedNavigation =
      !wasAlreadyTerminated && !deliveryInProgress && requestToProcess && this._sequencingService
        ? this._sequencingService.prepareNavigationRequest(
            requestToProcess,
            targetForProcessing,
            exitType,
          )
        : null;

    const result = this.terminate("Terminate", true);

    if (result !== global_constants.SCORM_TRUE && preparedNavigation && this._sequencingService) {
      this._sequencingService.cancelPreparedNavigation(preparedNavigation);
    }

    if (
      result === global_constants.SCORM_TRUE &&
      !wasAlreadyTerminated &&
      (this.settings.accumulateSessionTimeOnTerminate === true ||
        this._sequencingService?.getSequencingState().isInitialized === true)
    ) {
      // terminate() has already serialized the completed session. Preserve that
      // total when opted in, or when active sequencing needs it across delivery
      // resets of SCO-local CMI data.
      // @spec SCORM 2004 4th Ed. RTE 4.2.24 / 4.2.28
      this.cmi.accumulateSessionTime();
    }

    if (result === global_constants.SCORM_TRUE && !wasAlreadyTerminated && !deliveryInProgress) {
      let navigationHandled = false;
      let sequencingAttempted = false;
      let processedSequencingRequest: string | null = null;

      if (this._sequencingService) {
        try {
          if (requestToProcess) {
            // A prepared request proves that the internal engine was available
            // and accepted the request for completion. If preparation was not
            // possible, only invoke the compatibility path when the internal
            // engine itself is unavailable; an available engine rejecting a
            // request must not duplicate it through legacy listeners.
            const sequencingAvailable =
              this._sequencingService.getSequencingState().isInitialized === true;
            sequencingAttempted = sequencingAvailable;
            navigationHandled = preparedNavigation
              ? this._sequencingService.completeNavigationRequest(preparedNavigation)
              : this._sequencingService.processNavigationRequest(
                  requestToProcess,
                  targetForProcessing,
                  exitType,
                );
            processedSequencingRequest = requestToProcess;
          }
        } catch (error) {
          this.apiLog("lmsFinish", `Sequencing navigation failed: ${error}`, LogLevelEnum.WARN);
          navigationHandled = false;
        }
      }

      if (!navigationHandled && !sequencingAttempted) {
        if (pendingNavRequest !== "_none_") {
          const navActions: { [key: string]: string } = {
            continue: "SequenceNext",
            previous: "SequencePrevious",
            choice: "SequenceChoice",
            jump: "SequenceJump",
            exit: "SequenceExit",
            exitAll: "SequenceExitAll",
            abandon: "SequenceAbandon",
            abandonAll: "SequenceAbandonAll",
          };

          const action = navActions[normalizedRequest];
          if (action) {
            this.processListeners(action, "adl.nav.request", normalizedTarget);
          }
        } else if (this.settings.autoProgress) {
          this.processListeners("SequenceNext", undefined, "next");
        }
      }

      if (
        this._sequencingService &&
        processedSequencingRequest &&
        ["exitAll", "abandonAll", "suspendAll"].includes(processedSequencingRequest)
      ) {
        this._sequencingService.terminate();
      }

      this.adl.nav.request = "_none_";
    }

    if (result === global_constants.SCORM_TRUE && !wasAlreadyTerminated) {
      const autoSaveOn = this.settings.sequencingStatePersistence?.autoSaveOn ?? "commit";
      if (this.settings.sequencingStatePersistence && ["commit", "navigate"].includes(autoSaveOn)) {
        this.autoSaveSequencingState("lmsFinish");
      }
    }

    return result;
  }

  /**
   * GetValue - Retrieves a value from the CMI data model
   *
   * @param {string} CMIElement - The CMI element path
   * @return {string} The value of the element, or empty string
   */
  lmsGetValue(CMIElement: string): string {
    // Per SCORM 2004 RTE Section 3.1.3.3: state checks must come before
    // any data model element processing
    if (this.isTerminated()) {
      this.lastErrorCode = String(scorm2004_errors.RETRIEVE_AFTER_TERM);
      return "";
    }
    if (!this.isInitialized()) {
      this.lastErrorCode = String(scorm2004_errors.RETRIEVE_BEFORE_INIT);
      return "";
    }

    if (CMIElement === "adl.nav.request") {
      this.throwSCORMError(
        CMIElement,
        scorm2004_errors.WRITE_ONLY_ELEMENT,
        "adl.nav.request is write-only",
      );
      return "";
    }

    const adlNavRequestRegex =
      "^adl\\.nav\\.request_valid\\.(choice|jump)\\.{target=([a-zA-Z0-9-_]+)}$";
    if (stringMatches(CMIElement, adlNavRequestRegex)) {
      const matches = CMIElement.match(adlNavRequestRegex);
      if (matches) {
        const request = matches[1];
        const target = matches[2]?.replace(/{target=/g, "").replace(/}/g, "") || "";
        if (request === "choice" || request === "jump") {
          const overallProcess = this._sequencing?.overallSequencingProcess;

          if (this.settings.scoItemIdValidator) {
            return String(this.settings.scoItemIdValidator(target));
          }

          // There is no jump predictor on OverallSequencingProcess, so "jump"
          // requests fall through to the extracted SCO item ID check below.
          if (overallProcess?.predictChoiceEnabled && request === "choice") {
            return overallProcess.predictChoiceEnabled(target) ? "true" : "false";
          } else {
            if (this._extractedScoItemIds.length > 0) {
              return String(this._extractedScoItemIds.includes(target));
            }
            return String(this.settings?.scoItemIds?.includes(target));
          }
        }
      }
    }

    if (CMIElement === "cmi.completion_status") {
      return this._cmiHandler.evaluateCompletionStatus();
    }

    if (CMIElement === "cmi.success_status") {
      return this._cmiHandler.evaluateSuccessStatus();
    }

    return this.getValue("GetValue", true, CMIElement);
  }

  /**
   * SetValue - Sets a value in the CMI data model
   *
   * @param {string} CMIElement - The CMI element path
   * @param {any} value - The value to set
   * @return {string} "true" or "false"
   */
  lmsSetValue(CMIElement: string, value: any): string {
    const oldValue = this._peekCMIValue(CMIElement);

    const result = this.setValue("SetValue", "Commit", true, CMIElement, value);

    if (result === global_constants.SCORM_TRUE) {
      // @spec SCORM 2004 4th Ed. RTE 3.1.4.4 / 4.2.17 - remember writes
      // made by the SCO during this attempt, excluding LMS launch seeding.
      this._runtimeSetCMIElements.add(CMIElement);
    }

    if (result === global_constants.SCORM_TRUE && this._sequencingService) {
      try {
        this._sequencingService.triggerRollupOnCMIChange(CMIElement, oldValue, value);
      } catch (rollupError) {
        console.warn(`Sequencing rollup failed for ${CMIElement}: ${rollupError}`);
      }
    }

    if (
      result === global_constants.SCORM_TRUE &&
      this.settings.sequencingStatePersistence?.autoSaveOn === "setValue"
    ) {
      const sequencingElements = [
        "cmi.completion_status",
        "cmi.success_status",
        "cmi.score.scaled",
        "cmi.objectives",
        "adl.nav.request",
      ];

      if (sequencingElements.some((element) => CMIElement.startsWith(element))) {
        this.saveSequencingState().catch(() => {
          this.apiLog("lmsSetValue", "Failed to auto-save sequencing state", LogLevelEnum.WARN);
        });
      }
    }

    return result;
  }

  /**
   * Commit - Requests immediate persistence of data to the LMS
   *
   * @param {string} parameter - Must be an empty string per SCORM 2004 specification
   * @return {string} "true" or "false"
   */
  lmsCommit(parameter: string = ""): string {
    if (parameter !== "") {
      this.throwSCORMError("api", this._error_codes.ARGUMENT_ERROR);
      return global_constants.SCORM_FALSE;
    }

    if (this.settings.throttleCommits) {
      this.scheduleCommit(500, "Commit");
      if (
        this.settings.sequencingStatePersistence &&
        (this.settings.sequencingStatePersistence.autoSaveOn ?? "commit") === "commit"
      ) {
        this.autoSaveSequencingState("lmsCommit");
      }
      return global_constants.SCORM_TRUE;
    } else {
      const result = this.commit("Commit", true);

      if (
        result === global_constants.SCORM_TRUE &&
        this.settings.sequencingStatePersistence &&
        (this.settings.sequencingStatePersistence.autoSaveOn ?? "commit") === "commit"
      ) {
        this.autoSaveSequencingState("lmsCommit");
      }

      return result;
    }
  }

  /**
   * GetLastError - Returns the error code from the last API call
   * @return {string} Error code as a string
   */
  lmsGetLastError(): string {
    return this.getLastError("GetLastError");
  }

  /**
   * GetErrorString - Returns a short description for an error code
   * @param {string|number} CMIErrorCode - The error code
   * @return {string} Short error description
   */
  lmsGetErrorString(CMIErrorCode: string | number): string {
    return this.getErrorString("GetErrorString", CMIErrorCode);
  }

  /**
   * GetDiagnostic - Returns detailed diagnostic information for an error
   * @param {string|number} CMIErrorCode - The error code
   * @return {string} Detailed diagnostic information
   */
  lmsGetDiagnostic(CMIErrorCode: string | number): string {
    return this.getDiagnostic("GetDiagnostic", CMIErrorCode);
  }

  /**
   * Sets a value on the CMI Object
   * @param {string} CMIElement
   * @param {any} value
   * @return {string}
   */
  /**
   * Restore a previously stored CMI data model without republishing it to global objectives.
   *
   * loadFromJSON replays stored values through setCMIValue, which is the same entry point content
   * uses. That makes an LMS-side restore indistinguishable from a fresh SetValue, so restoring an
   * attempt whose cmi.objectives happens to name a global objective id would push those stored
   * values back into the sequencing global objective map — overwriting whatever later attempts
   * wrote and rolling shared objectives back to the moment this attempt was saved. Restored data is
   * a replay of writes that already happened, so it must not trigger the write path again.
   *
   * @spec SCORM 2004 4th Ed. SN 3.10.3 - global objective writes originate from objective mapInfo,
   *   not from re-loading persisted run-time data
   */
  override loadFromJSON(json: StringKeyMap, CMIElement: string = ""): void {
    this._restoringFromJSON = true;
    try {
      super.loadFromJSON(json, CMIElement);
    } finally {
      this._restoringFromJSON = false;
    }
  }

  override setCMIValue(CMIElement: string, value: any): string {
    if (stringMatches(CMIElement, "cmi\\.objectives\\.\\d+")) {
      const parts = CMIElement.split(".");
      const index = Number(parts[2]);
      const element_base = `cmi.objectives.${index}`;

      let objective_id;
      const setting_id = stringMatches(CMIElement, "cmi\\.objectives\\.\\d+\\.id");

      if (setting_id) {
        objective_id = value;
      } else {
        const objective = this.cmi.objectives.findObjectiveByIndex(index);
        objective_id = objective ? objective.id : undefined;
      }

      // Suppress the publish only while replaying stored data into an API that also restores
      // sequencing state. There the sequencing global objective map has already been loaded and is
      // the fresher of the two, so republishing would overwrite it. Without sequencing state
      // persistence there is no fresher map to protect, and this remains the only path that seeds
      // global objectives from a restored cmi.objectives entry.
      const suppressGlobalPublish =
        this._restoringFromJSON && Boolean(this.settings.sequencingStatePersistence);
      const is_global =
        !suppressGlobalPublish &&
        objective_id &&
        this._globalObjectiveManager.isHostDeclaredGlobalObjectiveId(objective_id);

      if (is_global && this.currentActivityAllowsGlobalObjectiveWrites()) {
        const { index: global_index } =
          this._globalObjectiveManager.findOrCreateGlobalObjective(objective_id);

        const global_element = CMIElement.replace(
          element_base,
          `_globalObjectives.${global_index}`,
        );
        this._commonSetCMIValue("SetGlobalObjectiveValue", true, global_element, value);

        const updatedObjective = this._globalObjectiveManager.globalObjectives[global_index];
        if (objective_id && updatedObjective) {
          this._globalObjectiveManager.updateGlobalObjectiveFromCMI(objective_id, updatedObjective);
        }
      }
    }
    return this._commonSetCMIValue("SetValue", true, CMIElement, value);
  }

  /**
   * Return whether the current activity can update shared global objectives.
   *
   * @spec SCORM 2004 4th Ed. SN 3.13.1 Tracked - when False, the LMS
   * "does not initialize, manage or access any tracking status information".
   */
  private currentActivityAllowsGlobalObjectiveWrites(): boolean {
    return this._sequencing?.getCurrentActivity()?.sequencingControls.tracked !== false;
  }

  /**
   * Gets or builds a new child element to add to the array
   * @param {string} CMIElement
   * @param {any} value
   * @param {boolean} foundFirstIndex
   * @return {BaseCMI|null}
   */
  getChildElement(CMIElement: string, value: any, foundFirstIndex: boolean): BaseCMI | null {
    return this._cmiHandler.getChildElement(CMIElement, value, foundFirstIndex);
  }

  /**
   * Validate correct response
   * @param {string} CMIElement
   * @param {*} value
   */
  validateCorrectResponse(CMIElement: string, value: any) {
    const parts = CMIElement.split(".");
    const index = Number(parts[2]);
    const interaction = this.cmi.interactions.childArray[index] as
      CMIInteractionsObject | undefined;

    if (!interaction) {
      this.throwSCORMError(CMIElement, scorm2004_errors.DEPENDENCY_NOT_ESTABLISHED, CMIElement);
      return;
    }

    this._responseValidator.validateCorrectResponse(CMIElement, interaction, value);
  }

  /**
   * Silently peeks at a CMI value without triggering error logging or
   * mutating lastErrorCode. Returns null if the element doesn't exist.
   * Used internally to capture old values before SetValue overwrites them.
   *
   * @param {string} CMIElement - dot-delimited CMI path (e.g. "cmi.interactions.0.id")
   * @return {*} the current value, or null if the path doesn't resolve
   */
  private _peekCMIValue(CMIElement: string): any {
    if (!CMIElement || typeof CMIElement !== "string") {
      return null;
    }

    const segments = CMIElement.split(".");
    let refObject: any = this;

    for (let i = 0; i < segments.length; i++) {
      const attribute = segments[i] as string;

      if (refObject == null) {
        return null;
      }

      try {
        refObject = refObject[attribute];
      } catch {
        // Property getter threw (e.g. write-only elements like session_time)
        return null;
      }

      if (refObject instanceof CMIArray) {
        // Next segment may be a numeric index into childArray, or a
        // property like _count/_children accessed on the array itself.
        const nextIndex = i + 1;
        if (nextIndex >= segments.length) {
          return refObject;
        }

        const nextSegment = segments[nextIndex] as string;
        const index = Number(nextSegment);

        if (!Number.isNaN(index) && Number.isInteger(index) && index >= 0) {
          if (index >= refObject.childArray.length) {
            return null;
          }
          refObject = refObject.childArray[index];
          i++; // skip the index segment
        }
        // If nextSegment is not a valid numeric index (e.g. _count),
        // fall through and let the next iteration access it as a
        // normal property on the CMIArray instance.
      }
    }

    return refObject ?? null;
  }

  /**
   * Gets a value from the CMI Object
   * @param {string} CMIElement
   * @return {*}
   */
  override getCMIValue(CMIElement: string): any {
    return this._commonGetCMIValue("GetValue", true, CMIElement);
  }

  /**
   * Raises 403 (VALUE_NOT_INITIALIZED) when an implemented, no-default element is
   * read before it has ever been set, per IEEE 1484.11.2 / SCORM 2004 RTE.
   *
   * Invoked by BaseAPI.getValue after an otherwise-successful resolution, so it
   * only sees values the data model already returned cleanly. The decision is
   * deliberately gated to this public boundary (never the getters) to keep
   * serialization/commit/rollup — which read the getters directly — unaffected.
   *
   * A 403 is raised only when all hold:
   *  - the resolved value is "" — any non-empty value is by definition set,
   *    including values written by internal sequencing/activity-tree paths that
   *    bypass _commonSetCMIValue (those only ever write non-empty values);
   *  - the element was never set — _setCMIElements records every successful
   *    SetValue, loadFromJSON, and global-objective restore (all route through
   *    _commonSetCMIValue), so an explicit SetValue(x, "") still reads back as
   *    "" with code 0; and
   *  - the element has no spec-defined default ({@link NO_DEFAULT_2004_ELEMENTS}).
   *
   * @param {string} CMIElement
   * @param {*} returnValue - the value getCMIValue resolved
   * @protected
   */
  protected override checkUninitializedGet(CMIElement: string, returnValue: any): void {
    if (returnValue !== "") return;
    if (this._setCMIElements.has(CMIElement)) return;
    if (this.adl.isConfiguredSharedDataElement(CMIElement)) return;
    if (!NO_DEFAULT_2004_ELEMENTS.has(normalizeCMIIndices(CMIElement))) return;

    this.throwSCORMError(
      CMIElement,
      this._error_codes.VALUE_NOT_INITIALIZED ?? 403,
      `The data model element passed to GetValue (${CMIElement}) has not been initialized.`,
      this.settings.uninitializedGetLogLevel,
    );
  }

  /**
   * Returns the message that corresponds to errorNumber.
   * @param {(string|number)} errorNumber
   * @param {boolean} detail
   * @return {string}
   */
  override getLmsErrorMessageDetails(errorNumber: string | number, detail: boolean): string {
    let basicMessage = "";
    let detailMessage = "";

    errorNumber = String(errorNumber);
    const errorDescription = scorm2004_constants.error_descriptions[errorNumber];
    if (errorDescription) {
      basicMessage = errorDescription.basicMessage;
      detailMessage = errorDescription.detailMessage;
    }

    return detail ? detailMessage : basicMessage;
  }

  /**
   * Replace the whole API with another
   * @param {Scorm2004API} newAPI
   */
  replaceWithAnotherScormAPI(newAPI: Scorm2004API) {
    this.cmi = newAPI.cmi;
    this.adl = newAPI.adl;
  }

  /**
   * Render the cmi object to the proper format for LMS commit
   * @param {boolean} terminateCommit
   * @param {boolean} includeTotalTime
   * @return {object|Array}
   */
  renderCommitCMI(
    terminateCommit: boolean,
    includeTotalTime: boolean = false,
  ): StringKeyMap | Array<any> {
    return this._dataSerializer.renderCommitCMI(terminateCommit, includeTotalTime);
  }

  /**
   * Render the cmi object to the proper format for LMS commit
   * @param {boolean} terminateCommit
   * @param {boolean} includeTotalTime
   * @return {CommitObject}
   */
  renderCommitObject(terminateCommit: boolean, includeTotalTime: boolean = false): CommitObject {
    return this._dataSerializer.renderCommitObject(terminateCommit, includeTotalTime);
  }

  /**
   * Attempts to store the data to the LMS
   * @param {boolean} terminateCommit
   * @param {CommitTrigger} [trigger] - What initiated the commit
   * @return {ResultObject}
   */
  storeData(terminateCommit: boolean, trigger?: CommitTrigger): ResultObject {
    if (terminateCommit) {
      if (this.cmi.mode === "normal") {
        if (this.cmi.credit === "credit") {
          if (this.cmi.completion_threshold && this.cmi.progress_measure) {
            if (
              parseFloat(this.cmi.progress_measure) >= parseFloat(this.cmi.completion_threshold)
            ) {
              this.cmi.completion_status = "completed";
            } else {
              this.cmi.completion_status = "incomplete";
            }
          }
          if (this.cmi.scaled_passing_score && this.cmi.score.scaled) {
            if (parseFloat(this.cmi.score.scaled) >= parseFloat(this.cmi.scaled_passing_score)) {
              this.cmi.success_status = "passed";
            } else {
              this.cmi.success_status = "failed";
            }
          }
        }
      }
    }

    let navRequest = false;
    if (
      this.adl.nav.request !==
        ((this.startingData?.adl as StringKeyMap)?.nav as StringKeyMap)?.request &&
      this.adl.nav.request !== "_none_"
    ) {
      navRequest = true;
    }

    const commitObject = this.getCommitObject(terminateCommit);
    // RTE Commit persists the SCO data model but does not end the activity attempt. Sequencing
    // tracking and objective maps are updated only by the End Attempt Process prepared before a
    // successful Terminate; abandon therefore leaves the reported attempt data unrecorded.
    // @spec SCORM 2004 4th Ed. SN TB.2.3 / UP.4 and TR SX-04a / SX-04b
    if (typeof this.settings.lmsCommitUrl === "string") {
      const result = this.processHttpRequest(
        this.settings.lmsCommitUrl,
        commitObject,
        terminateCommit,
        trigger,
      );

      if (
        navRequest &&
        result.navRequest !== undefined &&
        result.navRequest !== "" &&
        typeof result.navRequest === "string"
      ) {
        const parsed: ParsedNavigationRequest = parseNavigationRequest(result.navRequest);

        if (!parsed.valid) {
          this.apiLog(
            "storeData",
            `Invalid navigation request from LMS: ${parsed.error}`,
            LogLevelEnum.WARN,
          );
        } else {
          const navEventMap: { [key: string]: string } = {
            start: "SequenceStart",
            resumeAll: "SequenceResumeAll",
            continue: "SequenceNext",
            previous: "SequencePrevious",
            choice: "SequenceChoice",
            jump: "SequenceJump",
            exit: "SequenceExit",
            exitAll: "SequenceExitAll",
            abandon: "SequenceAbandon",
            abandonAll: "SequenceAbandonAll",
            suspendAll: "SequenceSuspendAll",
          };

          const eventName = navEventMap[parsed.command];
          if (eventName) {
            this.processListeners(eventName, "adl.nav.request", parsed.targetActivityId);
          }
        }
      } else if (result?.navRequest && !navRequest) {
        if (
          typeof result.navRequest === "object" &&
          Object.hasOwnProperty.call(result.navRequest, "name") &&
          result.navRequest.name
        ) {
          this.processListeners(result.navRequest.name as string, result.navRequest.data as string);
        }
      }

      return result;
    }

    return {
      result: global_constants.SCORM_TRUE,
      errorCode: 0,
    };
  }

  /**
   * Configure sequencing based on provided settings
   * @param {SequencingSettings} sequencingSettings
   */
  private configureSequencing(sequencingSettings: SequencingSettings): void {
    this._sequencingCollections = this._sequencingConfigBuilder.sanitizeSequencingCollections(
      sequencingSettings.collections,
    );
    this._activityTreeBuilder.setSequencingCollections(this._sequencingCollections);

    if (sequencingSettings.activityTree) {
      this.configureActivityTree(sequencingSettings.activityTree);
    }

    if (sequencingSettings.sequencingRules) {
      this.configureSequencingRules(sequencingSettings.sequencingRules);
    }

    if (sequencingSettings.sequencingControls) {
      this.configureSequencingControls(sequencingSettings.sequencingControls);
    }

    if (sequencingSettings.rollupRules) {
      this.configureRollupRules(sequencingSettings.rollupRules);
    }

    if (sequencingSettings.hideLmsUi) {
      this._sequencing.hideLmsUi = this._sequencingConfigBuilder.sanitizeHideLmsUi(
        sequencingSettings.hideLmsUi,
      );
    } else {
      this._sequencing.hideLmsUi = [];
    }

    if (sequencingSettings.auxiliaryResources) {
      this._sequencing.auxiliaryResources =
        this._sequencingConfigBuilder.sanitizeAuxiliaryResources(
          sequencingSettings.auxiliaryResources,
        );
    } else {
      this._sequencing.auxiliaryResources = [];
    }
  }

  /**
   * Configure activity tree based on provided settings
   * @param {ActivitySettings} activityTreeSettings
   */
  private configureActivityTree(activityTreeSettings: ActivitySettings): void {
    const rootActivity = this._activityTreeBuilder.createActivity(activityTreeSettings);
    const activityTree = this._sequencing.activityTree;
    activityTree.root = rootActivity;
    this._extractedScoItemIds = this._activityTreeBuilder.extractActivityIds(rootActivity);
  }

  /**
   * Configure sequencing rules based on provided settings
   * @param {SequencingRulesSettings} sequencingRulesSettings
   */
  private configureSequencingRules(sequencingRulesSettings: SequencingRulesSettings): void {
    this._sequencingConfigBuilder.applySequencingRulesSettings(
      this._sequencing.sequencingRules,
      sequencingRulesSettings,
    );
  }

  /**
   * Configure sequencing controls based on provided settings
   * @param {SequencingControlsSettings} sequencingControlsSettings
   */
  private configureSequencingControls(
    sequencingControlsSettings: SequencingControlsSettings,
  ): void {
    this._sequencingConfigBuilder.applySequencingControlsSettings(
      this._sequencing.sequencingControls,
      sequencingControlsSettings,
    );
  }

  /**
   * Configure rollup rules based on provided settings
   * @param {RollupRulesSettings} rollupRulesSettings
   */
  private configureRollupRules(rollupRulesSettings: RollupRulesSettings): void {
    this._sequencingConfigBuilder.applyRollupRulesSettings(
      this._sequencing.rollupRules,
      rollupRulesSettings,
    );
  }

  /**
   * Build the context handed to SequencingStatePersistence.
   *
   * `learnerId` is a getter, not a snapshot: the constructor builds this context before the
   * host has loaded cmi.learner_id, and `this.cmi` can be replaced by reset() or a resume,
   * so the persistence layer must read the current value each time it saves or loads.
   *
   * Because the value is live, a loadSequencingState() issued before the host sets
   * cmi.learner_id keys its in-flight dedupe on "unknown", and a second load issued after the
   * id is set keys on the real id, so the two are not coalesced. The learner id cannot change
   * after Initialize(), so the window is limited to hosts that load before initializing.
   * @spec SCORM 2004 4th Ed. RTE 4.2.11 - cmi.learner_id identifies the learner; persisted
   * sequencing state must be attributed to that learner, not to a placeholder.
   * @return {PersistenceContext}
   */
  private createPersistenceContext(): PersistenceContext {
    const api = this;
    return {
      getSettings: () => this.settings,
      apiLog: this.apiLog.bind(this),
      adl: this.adl,
      sequencing: this._sequencing,
      sequencingService: this._sequencingService,
      get learnerId(): string {
        return api.cmi.learner_id;
      },
    };
  }

  /**
   * Initialize the sequencing service
   * @param {Settings} settings
   */
  private initializeSequencingService(settings?: Settings): void {
    try {
      const sequencingConfig: SequencingConfiguration = {
        autoRollupOnCMIChange: settings?.sequencing?.autoRollupOnCMIChange ?? false,
        autoProgressOnCompletion: settings?.sequencing?.autoProgressOnCompletion ?? false,
        validateNavigationRequests: settings?.sequencing?.validateNavigationRequests ?? true,
        enableEventSystem: settings?.sequencing?.enableEventSystem ?? true,
        logLevel: settings?.sequencing?.logLevel ?? "info",
        wasCMIElementSetByContent: (element) => this._runtimeSetCMIElements.has(element),
      };

      this._sequencingService = new SequencingService(
        this._sequencing,
        this.cmi,
        this.adl,
        this.eventService || this,
        this.loggingService,
        sequencingConfig,
      );

      // @spec SCORM 2004 4th Ed. SN DB.2 - delivery establishes the current
      // activity before the SCO can call Initialize.
      // @spec SCORM 2004 4th Ed. RTE 4.2.5 / 4.2.17 - launch-static CMI
      // elements are initialized for the activity's associated SCO.
      this._sequencingService.setEventListeners(
        this.buildSequencingEventListeners(settings?.sequencing?.eventListeners),
      );

      // Update context references after sequencing service is created
      this._globalObjectiveManager.updateSequencingService(this._sequencingService);
      this._dataSerializer.updateSequencingService(this._sequencingService);

      // Initialize state persistence
      if (settings?.sequencingStatePersistence) {
        this._statePersistence = new SequencingStatePersistence(
          this.createPersistenceContext(),
          this._globalObjectiveManager,
        );
      }

      this._globalObjectiveManager.syncGlobalObjectiveIdsFromSequencing();
    } catch (error) {
      console.warn("Failed to initialize sequencing service:", error);
      this._sequencingService = null;
    }
  }

  /**
   * Wrap LMS-provided sequencing listeners with API-owned delivery bookkeeping.
   *
   * @spec SCORM 2004 4th Ed. SN DB.2 - Content Delivery Environment Process
   * @spec SCORM 2004 4th Ed. RTE 4.2.5, Table 4.2.5a - cmi.completion_threshold
   * @spec SCORM 2004 4th Ed. RTE 4.2.17, Table 4.2.17a - cmi.objectives
   */
  private buildSequencingEventListeners(
    listeners?: SequencingEventListeners,
  ): SequencingEventListeners {
    return {
      ...listeners,
      onActivityDelivery: (activity) => {
        this.applyDeliveredActivityLaunchData(activity as Activity);
        listeners?.onActivityDelivery?.(activity);
      },
    };
  }

  /**
   * Get the sequencing service
   * @return {SequencingService | null}
   */
  public getSequencingService(): SequencingService | null {
    return this._sequencingService;
  }

  /**
   * Preview a host Continue/Previous click without ending the SCO or changing its
   * tracking data. The result reflects currently reported CMI, not future SCO writes.
   * This host extension does not change SCORM's adl.nav.request_valid semantics.
   */
  public previewNavigationRequest(request: "continue" | "previous"): NavigationPreview {
    if (this.isInitialized() && this._sequencingService) {
      return this._sequencingService.previewNavigationRequest(request);
    }
    return {
      outcome: "unknown",
      targetActivityId: null,
      endSequencingSession: false,
      exception: null,
    };
  }

  /**
   * Set sequencing event listeners
   * @param {SequencingEventListeners} listeners
   *
   * @spec SCORM 2004 4th Ed. SN DB.2 - Content Delivery Environment Process
   * @spec SCORM 2004 4th Ed. RTE 4.2.5 / 4.2.17 - launch-static CMI data
   */
  public setSequencingEventListeners(listeners: SequencingEventListeners): void {
    if (this._sequencingService) {
      this._sequencingService.setEventListeners(this.buildSequencingEventListeners(listeners));
    }
  }

  /**
   * Update sequencing configuration
   * @param {SequencingConfiguration} config
   */
  public updateSequencingConfiguration(config: SequencingConfiguration): void {
    if (this._sequencingService) {
      this._sequencingService.updateConfiguration(config);
    }
  }

  /**
   * Get current sequencing state information
   * @return {object}
   */
  public getSequencingState(): any {
    if (this._sequencingService) {
      return this._sequencingService.getSequencingState();
    }
    return {
      isInitialized: false,
      isActive: false,
      currentActivity: null,
      rootActivity: this._sequencing.getRootActivity(),
      lastSequencingResult: null,
    };
  }

  /**
   * Process a navigation request directly
   * @param {string} request
   * @param {string} targetActivityId
   * @return {boolean}
   */
  public processNavigationRequest(request: string, targetActivityId?: string): boolean {
    if (this._sequencingService) {
      const result = this._sequencingService.processNavigationRequest(request, targetActivityId);
      if (result && this.settings.sequencingStatePersistence?.autoSaveOn === "navigate") {
        this.autoSaveSequencingState("processNavigationRequest");
      }
      return result;
    }
    return false;
  }

  private autoSaveSequencingState(source: string): void {
    this.saveSequencingState()
      .then((saved) => {
        if (!saved) {
          this.apiLog(source, "Failed to auto-save sequencing state", LogLevelEnum.WARN);
        }
      })
      .catch(() => {
        this.apiLog(source, "Failed to auto-save sequencing state", LogLevelEnum.WARN);
      });
  }

  /**
   * Reset sequencing state explicitly
   */
  public resetSequencingState(): void {
    this._sequencing?.reset();
    this._sequencingService?.setEventListeners({});
  }

  /**
   * Get tracking data for a specific activity
   * attemptCompletionAmount includes completion measure rollup for clusters.
   * Only use it when attemptCompletionAmountStatus is true; false means unknown.
   * @param {string} activityId
   * @return {object | null}
   */
  public getActivityTrackingData(activityId: string): {
    completionStatus: string;
    successStatus: string;
    progressMeasure: number | null;
    attemptCompletionAmount: number;
    attemptCompletionAmountStatus: boolean;
    score: number | null;
  } | null {
    if (!this._sequencing?.activityTree) {
      return null;
    }

    const activity = this._sequencing.activityTree.getActivity(activityId);
    if (!activity) {
      return null;
    }

    return {
      completionStatus: activity.completionStatus || "unknown",
      successStatus: activity.successStatus || "unknown",
      progressMeasure: activity.progressMeasure ?? null,
      attemptCompletionAmount: activity.attemptCompletionAmount,
      attemptCompletionAmountStatus: activity.attemptCompletionAmountStatus,
      score: activity.objectiveMeasureStatus ? activity.objectiveNormalizedMeasure : null,
    };
  }

  /**
   * Save current sequencing state to persistent storage
   * @param {Partial<SequencingStateMetadata>} metadata
   * @return {Promise<boolean>}
   */
  public async saveSequencingState(metadata?: Partial<SequencingStateMetadata>): Promise<boolean> {
    if (this._statePersistence) {
      return this._statePersistence.saveSequencingState(metadata);
    }

    if (!this.settings.sequencingStatePersistence) {
      this.apiLog(
        "saveSequencingState",
        "No persistence configuration provided",
        LogLevelEnum.WARN,
      );
      return false;
    }

    // Fallback: create persistence on the fly
    const persistence = new SequencingStatePersistence(
      this.createPersistenceContext(),
      this._globalObjectiveManager,
    );
    return persistence.saveSequencingState(metadata);
  }

  /**
   * Load sequencing state from persistent storage
   * @param {Partial<SequencingStateMetadata>} metadata
   * @return {Promise<boolean>}
   */
  public async loadSequencingState(metadata?: Partial<SequencingStateMetadata>): Promise<boolean> {
    if (this._statePersistence) {
      return this._statePersistence.loadSequencingState(metadata);
    }

    if (!this.settings.sequencingStatePersistence) {
      this.apiLog(
        "loadSequencingState",
        "No persistence configuration provided",
        LogLevelEnum.WARN,
      );
      return false;
    }

    // Fallback: create persistence on the fly
    const persistence = new SequencingStatePersistence(
      this.createPersistenceContext(),
      this._globalObjectiveManager,
    );
    return persistence.loadSequencingState(metadata);
  }

  /**
   * Serialize current sequencing state to JSON string
   * @return {string} Serialized state
   */
  public serializeSequencingState(): string {
    if (this._statePersistence) {
      return this._statePersistence.serializeSequencingState();
    }

    // Fallback: create persistence on the fly
    const persistence = new SequencingStatePersistence(
      this.createPersistenceContext(),
      this._globalObjectiveManager,
    );
    return persistence.serializeSequencingState();
  }

  /**
   * Deserialize sequencing state from JSON string
   * @param {string} stateData - Serialized state data
   * @return {boolean} Success status
   */
  public deserializeSequencingState(stateData: string): boolean {
    if (this._statePersistence) {
      return this._statePersistence.deserializeSequencingState(stateData);
    }

    // Fallback: create persistence on the fly
    const persistence = new SequencingStatePersistence(
      this.createPersistenceContext(),
      this._globalObjectiveManager,
    );
    return persistence.deserializeSequencingState(stateData);
  }

  /**
   * Determines the appropriate cmi.entry value based on previous exit state.
   * @param {string} previousExit
   * @param {boolean} hasSuspendData
   * @return {string}
   */
  public determineEntryValue(previousExit: string, hasSuspendData: boolean): string {
    return this._dataSerializer.determineEntryValue(previousExit, hasSuspendData);
  }
}

export default Scorm2004API;
